import React from 'react';

import classNames from 'classnames';

import { EM_DASH } from '../time_report/timeReportFormat';
import { TimeReportKpiTile } from '../time_report/TimeReportKpis';

import { COLUMN_DEFINITIONS, formatOverviewPercent, OverviewMoney, projectCount } from './timeOverviewFormat';
import TimeOverviewInfoTip from './TimeOverviewInfoTip';

function KpiTitle({ columnKey }) {
    const { label, definition } = COLUMN_DEFINITIONS[columnKey];

    return (
        <span className="tw-inline-flex tw-items-center tw-gap-x-1.5">
            {label}
            <TimeOverviewInfoTip label={label} definition={definition} className="tw-text-blue-75" />
        </span>
    );
}

/** Context fragments joined with commas, skipping the ones that have nothing to say. */
function joined(parts) {
    return parts.filter(Boolean).map((part, index) => (
        <React.Fragment key={index}>
            {index > 0 && ', '}
            {part}
        </React.Fragment>
    ));
}

/**
 * How much of the budgeted cost has been spent: a bar capped at full, turning
 * red once spend passes the cost it was budgeted at.
 */
function CostProgress({ percent }) {
    if (percent === null || percent === undefined) {
        return null;
    }

    return (
        <div className="tw-h-1.5 tw-mt-2 tw-rounded-full tw-bg-charcoal-15 tw-overflow-hidden">
            <div className={classNames('tw-h-full', percent > 100 ? 'tw-bg-red' : 'tw-bg-blue')} style={{ width: `${Math.min(Math.max(percent, 0), 100)}%` }} />
        </div>
    );
}

/**
 * The four headline figures, read from the total row so a card can never
 * disagree with the table beneath it. They follow Period and Status and count
 * every project, hidden or not; a column sort does not move them.
 */
export default function TimeOverviewKpis({ total }) {
    if (!total) {
        return (
            <div className="tw-grid tw-grid-cols-2 xl:tw-grid-cols-4 tw-gap-x-5 tw-gap-y-3">
                {[0, 1, 2, 3].map((index) => (
                    <TimeReportKpiTile key={index} title={EM_DASH} value={EM_DASH} />
                ))}
            </div>
        );
    }

    // Coloured by its own sign, like every other figure in the report: a
    // positive margin that is behind target is still a profit.
    const marginColour = total.current_margin === null ? undefined : Math.round(total.current_margin) < 0 ? 'tw-text-red' : 'tw-text-green';

    const budgetContext = `${total.budgeted_count} of ${projectCount(total.project_count)} ${total.project_count === 1 ? 'has' : 'have'} a budget`;

    const budgetedMarginPercent = formatOverviewPercent(total.budgeted_margin_percent);
    const costContext =
        total.budgeted_margin === null ? null : (
            <>
                Budgeted margin <OverviewMoney value={total.budgeted_margin} />
                {budgetedMarginPercent !== null && ` (${budgetedMarginPercent})`}
            </>
        );

    const costToDatePercent = formatOverviewPercent(total.cost_to_date_percent);
    const costToDateContext = joined([
        costToDatePercent !== null && `${costToDatePercent} of cost`,
        <>
            charged <OverviewMoney value={total.charged} />
        </>,
    ]);

    const marginContext = joined([
        formatOverviewPercent(total.current_margin_percent),
        total.variance !== null && (
            <>
                <OverviewMoney value={total.variance} /> against budgeted margin of <OverviewMoney value={total.budgeted_margin} />
            </>
        ),
        Math.round(total.untargeted_current_margin) !== 0 && (
            <>
                includes <OverviewMoney value={total.untargeted_current_margin} /> from projects with no budgeted margin
            </>
        ),
    ]);

    return (
        <div className="tw-grid tw-grid-cols-2 xl:tw-grid-cols-4 tw-gap-x-5 tw-gap-y-3">
            <TimeReportKpiTile title={<KpiTitle columnKey="budget" />} value={<OverviewMoney value={total.budget} />} context={budgetContext} />
            <TimeReportKpiTile title={<KpiTitle columnKey="cost" />} value={<OverviewMoney value={total.cost} />} context={costContext} />
            <TimeReportKpiTile title={<KpiTitle columnKey="cost_to_date" />} value={<OverviewMoney value={total.cost_to_date} />} context={costToDateContext}>
                <CostProgress percent={total.cost_to_date_percent} />
            </TimeReportKpiTile>
            <TimeReportKpiTile
                title={<KpiTitle columnKey="current_margin" />}
                value={<OverviewMoney value={total.current_margin} negativeClassName="" />}
                valueClassName={marginColour}
                context={marginContext.length > 0 ? marginContext : null}
            />
        </div>
    );
}
